'use client';
import { createTheme } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import tailwindTheme from './../../tailwind.config';

const colors = tailwindTheme.theme.extend.colors;

const COLORS = {
  white: { main: colors.white },
  'sage-1': { main: colors.sage[1], contrastText: colors.sage[10] },
  'sage-2': { main: colors.sage[2], contrastText: colors.sage[10] },
  'sage-3': { main: colors.sage[3], contrastText: colors.sage[10] },
  'sage-4': { main: colors.sage[4], contrastText: colors.sage[10] },
  'sage-5': { main: colors.sage[5], contrastText: colors.sage[10] },
  'sage-6': { main: colors.sage[6], contrastText: '#fff' },
  'sage-7': { main: colors.sage[7], contrastText: '#fff' },
  'sage-8': { main: colors.sage[8], contrastText: '#fff' },
  'sage-9': { main: colors.sage[9], contrastText: '#fff' },
  'sage-10': { main: colors.sage[10], contrastText: '#fff' },
  'mint-1': { main: colors.mint[1], contrastText: colors.sage[10] },
  'mint-2': { main: colors.mint[2], contrastText: colors.sage[10] },
  'teal-1': { main: colors.teal[1], contrastText: '#fff' },
  'teal-2': { main: colors.teal[2], contrastText: '#fff' },
  'emerald-1': { main: colors.emerald[1], contrastText: colors.emerald[2] },
  'emerald-2': { main: colors.emerald[2], contrastText: '#fff' },
  'crimson-1': { main: colors.crimson[1], contrastText: colors.crimson[2] },
  'crimson-2': { main: colors.crimson[2], contrastText: '#fff' },
  'site-background': { main: colors['site-background'] },
  'dialog-backdrop': { main: colors['dialog-backdrop'] },
  'brand-green': { main: colors.brand.green, contrastText: '#fff' },
  'green-primary': { main: colors.green.primary, contrastText: '#fff' },
  'green-bg': { main: colors.green.bg, contrastText: '#fff' },
  'green-1': { main: colors.green[1], contrastText: '#fff' },
  'green-2': { main: colors.green[2], contrastText: '#fff' },
  'green-3': { main: colors.green[3], contrastText: '#fff' },
  'lavender-primary': { main: colors.lavender.primary, contrastText: '#fff' },
  'lavender-bg': { main: colors.lavender.bg, contrastText: '#fff' },
  'rose-primary': { main: colors.rose.primary, contrastText: '#fff' },
  'rose-bg': { main: colors.rose.bg, contrastText: '#fff' },
  'blue-primary': { main: colors.blue.primary, contrastText: '#fff' },
  'blue-bg': { main: colors.blue.bg, contrastText: '#fff' },
  'blue-highlight': { main: colors.blue.highlight, contrastText: '#fff' },
  'blue-1': { main: colors.blue[1], contrastText: '#fff' },
  'blue-2': { main: colors.blue[2], contrastText: '#fff' },
  'blue-3': { main: colors.blue[3], contrastText: '#fff' },
  'blue-4': { main: colors.blue[4], contrastText: '#fff' },
  'cream-primary': { main: colors.cream.primary, contrastText: '#fff' },
  'cream-bg': { main: colors.cream.bg, contrastText: '#fff' },
  'red-1': { main: colors.red[1], contrastText: '#fff' },
  'red-2': { main: colors.red[2], contrastText: '#fff' },
  'red-3': { main: colors.red[3], contrastText: '#fff' },
  'red-4': { main: colors.red[4], contrastText: '#fff' },
  'orange-1': { main: colors.orange[1], contrastText: '#fff' },
  'orange-2': { main: colors.orange[2], contrastText: '#fff' },
  'orange-3': { main: colors.orange[3], contrastText: '#fff' },
  'orange-4': { main: colors.orange[4], contrastText: '#fff' },
  grey: { ...grey },
  'grey-1': { main: colors.grey[1], contrastText: '#fff' },
  'grey-2': { main: colors.grey[2], contrastText: '#fff' },
  'grey-3': { main: colors.grey[3], contrastText: '#fff' },
  'grey-4': { main: colors.grey[4], contrastText: '#fff' },
  'grey-5': { main: colors.grey[5], contrastText: '#fff' },
  'grey-6': { main: colors.grey[6], contrastText: '#fff' },
  'grey-7': { main: colors.grey[7], contrastText: '#fff' },
  'grey-8': { main: colors.grey[8], contrastText: '#fff' },
  'grey-9': { main: colors.grey[9], contrastText: '#fff' },
};

const inputSx = {
  '& .MuiInputLabel-root:not(.Mui-focused):not(.MuiFormLabel-filled)': {
    transform: 'translate(20px, 10px) scale(1)',
    fontSize: '14px',
  },
  '& .MuiInputLabel-root.Mui-focused, & .MuiFormLabel-filled': {
    transform: 'translate(14px, -9px) scale(0.75)',
  },
  '& .MuiInputBase-root .MuiOutlinedInput-notchedOutline': {
    transition: 'border-color 0.2s',
  },
  '& .MuiInputBase-root:not(.Mui-disabled):hover .MuiOutlinedInput-notchedOutline':
    {
      borderColor: COLORS['grey-4'].main,
    },
  '& .MuiInputBase-root:not(.Mui-disabled).Mui-focused .MuiOutlinedInput-notchedOutline':
    {
      borderColor: COLORS['grey-4'].main,
      borderWidth: '2px',
    },
  '& .MuiInputBase-input': {
    color: COLORS['grey-8'].main,
    height: '1rem',
    fontSize: '1rem',
    padding: '0.75rem 1.5rem',
  },
  '& .MuiInputBase-root': {
    borderRadius: '24px',
  },
  '& .MuiFormLabel-root:not(.Mui-focused)': {
    color: COLORS['grey-5'].main,
  },
  '& .MuiFormLabel-root.Mui-focused': {
    color: COLORS['grey-6'].main,
  },
  '& .Mui-disabled': {
    cursor: 'not-allowed',
  },
};

export const theme = createTheme({
  palette: COLORS,
  shape: {
    // @ts-ignore
    rounded: tailwindTheme.theme.extend.borderRadius,
  },
  components: {
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
    },
    MuiSelect: {
      defaultProps: {
        sx: {
          '& .MuiInputBase-input': {
            color: COLORS['grey-8'].main,
          },
          '& .Mui-disabled, & .MuiInputBase-input.Mui-disabled': {
            cursor: 'not-allowed',
          },
        },
      },
    },
    MuiAutocomplete: {
      defaultProps: {
        sx: {
          '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
            padding: '0.25rem 0.75rem',
          },
        },
      },
    },
    MuiInputBase: {
      defaultProps: {
        sx: {
          '&': {
            borderRadius: '24px',
          },
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        // @ts-ignore
        color: 'grey-4',
        sx: inputSx,
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
          // @ts-ignore
          variant: 'outlined',
          // @ts-ignore
          color: 'grey-4',
          sx: inputSx,
        },
      },
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      '-apple-system',
      'BlinkMacSystemFont',
      'Segoe UI',
      'Roboto',
      'Oxygen',
      'Ubuntu',
      'Cantarell',
      'Fira Sans',
      'Droid Sans',
      'Helvetica Neue',
      'sans-serif',
    ].join(','),
  },
});
